import {
  useLayoutEffect,
  useRef,
  type FormEventHandler,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE_SELECTOR = [
  'button:not(:disabled)',
  '[href]',
  'input:not(:disabled):not([type="hidden"])',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableElements(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter((element) => {
    if (element.hidden || element.closest('[hidden], [aria-hidden="true"]')) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

interface DialogShellProps {
  readonly ariaLabel?: string;
  readonly children: ReactNode;
  readonly className: string;
  readonly closeButtonLabel?: string;
  readonly closeOnBackdrop?: boolean;
  readonly closeOnEscape?: boolean;
  readonly descriptionId?: string;
  readonly element?: 'div' | 'form' | 'section';
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
  readonly onSubmit?: FormEventHandler<HTMLFormElement>;
  readonly returnFocusTo?: HTMLElement | null | undefined;
  readonly role?: 'alertdialog' | 'dialog';
  readonly showCloseButton?: boolean;
  readonly titleId?: string;
}

export function DialogShell({
  ariaLabel,
  children,
  className,
  closeButtonLabel = 'Close dialog',
  closeOnBackdrop = false,
  closeOnEscape = true,
  descriptionId,
  element = 'div',
  initialFocusRef,
  onClose,
  onSubmit,
  returnFocusTo,
  role = 'dialog',
  showCloseButton = false,
  titleId,
}: DialogShellProps) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );

  if (!titleId && !ariaLabel) {
    throw new Error('DialogShell requires titleId or ariaLabel.');
  }

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const requested = initialFocusRef?.current;
    const target = requested?.isConnected ? requested : (focusableElements(dialog)[0] ?? dialog);
    target.focus();

    const previouslyFocused = previouslyFocusedRef.current;
    return () => {
      const anotherDialog = [...document.querySelectorAll('[data-app-dialog="true"]')].some(
        (candidate) => candidate !== dialog,
      );
      if (anotherDialog) return;
      const focusTarget = returnFocusTo ?? previouslyFocused;
      if (focusTarget?.isConnected && !focusTarget.matches(':disabled, [hidden]')) {
        focusTarget.focus();
      }
    };
  }, [initialFocusRef, returnFocusTo]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (closeOnEscape) onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const controls = focusableElements(dialog);
    if (controls.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = controls[0];
    const last = controls.at(-1);
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
      event.preventDefault();
      first?.focus();
    }
  };

  const dialogChildren = (
    <>
      {showCloseButton ? (
        <button
          aria-label={closeButtonLabel}
          className="dialog-shell-close"
          onClick={onClose}
          type="button"
        >
          &times;
        </button>
      ) : null}
      {children}
    </>
  );

  const commonProps = {
    'aria-describedby': descriptionId,
    'aria-label': ariaLabel,
    'aria-labelledby': titleId,
    'aria-modal': true as const,
    className,
    'data-app-dialog': 'true',
    onKeyDown: handleKeyDown,
    role,
    tabIndex: -1,
  };
  const setDialogRef = (node: HTMLElement | null) => {
    dialogRef.current = node;
  };

  const dialog =
    element === 'form' ? (
      <form {...commonProps} onSubmit={onSubmit} ref={setDialogRef}>
        {dialogChildren}
      </form>
    ) : element === 'section' ? (
      <section {...commonProps} ref={setDialogRef}>
        {dialogChildren}
      </section>
    ) : (
      <div {...commonProps} ref={setDialogRef}>
        {dialogChildren}
      </div>
    );

  return createPortal(
    <div
      className="new-map-dialog-backdrop"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      {dialog}
    </div>,
    document.body,
  );
}
